import { Component, Show } from 'solid-js';
import { ModusWcSliderCustomEvent } from '@trimble-oss/moduswebcomponents';
import { t } from '@trimble/i18n';

export interface RangeRowProps {
  readonly id: string;
  readonly labelKey: string;
  readonly min: number;
  readonly max: number;
  readonly step?: number;
  readonly value: number;
  readonly isDisabled?: boolean;
  readonly disabledTooltipKey?: string;
  readonly sliderTestId?: string;
  /**
   * Formats the numeric value for the right-aligned readout. Defaults to
   * a plain integer (no unit), so callers that need units (e.g. clip
   * distance) supply their own formatter — keeps this row reusable
   * across unit systems.
   */
  formatValue?(value: number): string;
  /** See {@link TransparencyRowProps.isUnlinked} for semantics. */
  readonly isUnlinked?: boolean;
  readonly relinkTooltipKey?: string;
  onChange(value: number): void;
  onRelink?(): void;
}

/**
 * One labelled numeric-range row in a visibility menu. Generalises
 * {@link TransparencyRow}: any min/max/step, any value formatter.
 * Used for clip distance and any future per-pane scalar controls that
 * aren't 0–100% opacity.
 */
export const RangeRow: Component<RangeRowProps> = (props) => {
  const formatted = (): string => {
    const fmt = props.formatValue ?? ((v: number) => `${Math.round(v)}`);
    return fmt(props.value);
  };

  const handleChange = (ev: ModusWcSliderCustomEvent<InputEvent>): void => {
    if (props.isDisabled) return;
    const raw = parseFloat((ev.detail.target as HTMLInputElement).value);
    if (isNaN(raw)) return;
    props.onChange(raw);
  };

  return (
    <div
      class={`flex flex-col gap-1 ${props.isDisabled ? 'opacity-50' : ''}`}
      data-testid={`visibility-menu-row-${props.id}`}
      title={
        props.isDisabled && props.disabledTooltipKey
          ? t(props.disabledTooltipKey)
          : undefined
      }
    >
      <div class="flex items-center justify-between gap-2">
        <span class="text-sm text-base-content">{t(props.labelKey)}</span>
        <div class="flex items-center gap-2">
          <div class="flex h-5 w-5 items-center justify-center">
            <Show when={props.isUnlinked && props.onRelink}>
              <button
                type="button"
                class="flex h-full w-full items-center justify-center rounded text-base-content hover:bg-[var(--modus-wc-color-base-100)]"
                data-testid={`visibility-relink-${props.id}`}
                onClick={() => props.onRelink?.()}
                title={t(
                  props.relinkTooltipKey ??
                    'mobileMapping.transparency.syncToGlobal'
                )}
                aria-label={t(
                  props.relinkTooltipKey ??
                    'mobileMapping.transparency.syncToGlobal'
                )}
              >
                <i class="icon-font tc-icon-sync" />
              </button>
            </Show>
          </div>
          <span class="min-w-[3.5rem] text-right text-sm text-base-content tabular-nums">
            {formatted()}
          </span>
        </div>
      </div>
      <div class="trimble-slider slider-horizontal w-full">
        <modus-wc-slider
          attr:data-testid={
            props.sliderTestId ?? `visibility-range-${props.id}`
          }
          class="w-full h-6 min-h-6"
          min={props.min}
          max={props.max}
          step={props.step ?? 1}
          value={props.value}
          size="sm"
          disabled={props.isDisabled}
          on:inputChange={handleChange}
        />
      </div>
    </div>
  );
};
